import { AxiosError } from "axios";

import api from "services/api";
import { IReponse } from "services/interface";
import { returnResponse } from "services/utils";

import { LogOperation } from "utils/entities/logOperation";

import { CreateAction, IProfile, ResponseActionAdded } from "./interfaces";

export interface TipoProfileData {
  active: boolean;
  customerId: number;
  id: number;
  name: string;
  description?: string;
  confidentialityLevel?: number;
}

export interface TipoProfileResponse {
  data: TipoProfileData[];
  errors: string[];
  message: string;
  ok: boolean;
}

export const profilesService = {
  getAllByCustomerId: async (
    status?: 0 | 1 | null,
  ): Promise<IReponse<Array<IProfile>>> => {
    try {
      const customerId = LogOperation.getCustomerId();

      if (customerId) {
        const { data } = await api.get(
          `/v1/UserCustomerProfile/GetAllByCustomerId?customerId=${customerId}${
            status !== null ? `&Active=${status}` : ""
          }`,
        );

        return {
          status: true,
          body: data.data,
        };
      } else {
        throw new Error("user not set on local storage");
      }
    } catch (error: any) {
      return {
        status: true,
        body: error.response,
      };
    }
  },
  getAllByCustomerId2: async (
    type: number,
  ): Promise<IReponse<Array<IProfile>>> => {
    try {
      const customerId = LogOperation.getCustomerId();

      if (customerId) {
        const { data } = await api.get(
          `/v1/UserCustomerProfile/GetAllByCustomerId2?customerId=${customerId}&type=${type}`,
        );

        return {
          status: true,
          body: data.data,
        };
      } else {
        throw new Error("user not set on local storage");
      }
    } catch (error: any) {
      return {
        status: true,
        body: error.response,
      };
    }
  },
  getFormList: async (customerProfileId: any) => {
    try {
      const customerId = LogOperation.getCustomerId();

      if (customerId) {
        const { data } = await api.get(
          `/v1/UserCustomerProfile/GetProfile?customerId=${customerId}&userCustomerProfileId=${customerProfileId}`,
        );

        return {
          status: true,
          body: data.data,
        };
      } else {
        throw new Error("user not set on local storage");
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  getAllProfiles: async (authToken: string | null) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const { data } = await api.get<TipoProfileResponse>(
        `/v1/UserCustomerProfile/GetProfile?customerId=${LogOperation.getCustomerId()}`,
        config,
      );

      return {
        status: true,
        data: data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },

  createProfile: async (authToken: string | null, profile: any) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    try {
      const body = {
        ...profile,
      };
      const { data } = await api.post<any>(`/v1/UserCustomerProfile/Add`, body);

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  editProfile: async (authToken: string | null, profile: any) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    try {
      const body = {
        ...profile,
        device: LogOperation.getDevice(),
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: LogOperation.getPoliceUnitId(),
        logUserId: LogOperation.getUserId(),
      };
      const { data } = await api.put<any>(
        `/v1/UserCustomerProfile/Update`,
        body,
      );

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  createRelationProfileAction: async (
    authToken: string | null,
    userCustomerProfileId: number,
    systemObjectCustomerId: number,
  ) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    if (!userCustomerProfileId || !systemObjectCustomerId) {
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };
    }

    try {
      const body = {
        userCustomerProfileId,
        systemObjectCustomerId,
        device: LogOperation.getDevice(),
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: LogOperation.getPoliceUnitId(),
        logUserId: LogOperation.getUserId(),
      };
      const { data } = await api.post<ResponseActionAdded>(
        `/v1/SystemObjectUserCustomerProfile/Add`,
        body,
      );

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },

  createActionByProfileRelation: async (
    authToken: string | null,
    arrayOfActions: CreateAction[],
  ) => {
    if (!authToken)
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    if (!arrayOfActions?.length)
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };

    const norm = arrayOfActions
      .map((a: any) => ({
        systemActionId: Number(a.systemActionId),
        soUserCustomerProfileId: Number(a.soUserCustomerProfileId),
      }))
      .filter(
        (a) =>
          Number.isFinite(a.systemActionId) &&
          Number.isFinite(a.soUserCustomerProfileId),
      );

    const seen = new Set<string>();
    const payload = norm.filter((action) => {
      const compositeKey = `${action.soUserCustomerProfileId}:${action.systemActionId}`;
      if (seen.has(compositeKey)) return false;
      seen.add(compositeKey);
      return true;
    });

    const config = { headers: { Authorization: `Bearer ${authToken}` } };

    try {
      throw new Error("skip AddAll");
    } catch {
      for (const a of payload) {
        await profilesService.createActionByProfileRelationAction(
          authToken,
          a as any,
        );
      }
      return { status: true, data: null };
    }
  },

  createActionByProfileRelationAction: async (
    authToken: string | null,
    action: CreateAction,
  ) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }
    if (!action) {
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };
    }

    const body = {
      soUserCustomerProfileId: Number(action.soUserCustomerProfileId),
      systemActionId: Number(action.systemActionId),
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
    };
    const config = { headers: { Authorization: `Bearer ${authToken}` } };

    try {
      const { data } = await api.post<ResponseActionAdded>(
        `/v1/SystemObjectUserCustomerProfileAction/Add`,
        body,
        config,
      );
      if (data?.ok === false) {
        const msg = (data?.errors?.[0]?.message || "").toLowerCase();
        const dup =
          msg.includes("ora-00001") || msg.includes("unique constraint");
        if (dup) return { status: true, data: null };
        return {
          status: false,
          message: data?.message || "Falha no Add",
          data: null,
        };
      }

      return { status: true, data: data?.data ?? null };
    } catch (error: any) {
      const emsg = (
        error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        ""
      ).toLowerCase();
      const dup =
        emsg.includes("ora-00001") || emsg.includes("unique constraint");
      if (dup) return { status: true, data: null };

      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });
        return { severity, status, message: message || "Error", data };
      }
      return { status: false, message: "Error", data: null, severity: "error" };
    }
  },

  changeProfileStatus: async (
    authToken: string,
    profileId: number,
    status: boolean,
  ) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    if (!profileId) {
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };
    }

    try {
      const { data } = await api.patch<any>(
        `/v1/UserCustomerProfile/Update`,
        [
          {
            op: "replace",
            value: status,
            path: "/active",
          },
        ],
        {
          params: {
            id: profileId,
          },
        },
      );

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  changeProfileNomeEDescricao: async (
    authToken: string,
    profileId: number,
    name: string,
    description: string,
    adminSwitch2: number,
    active: boolean,
    confidentialityLevel: number,
  ) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    if (!profileId || !name || !description) {
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };
    }

    try {
      const { data } = await api.patch<any>(
        `v1/UserCustomerProfile/Update`,
        [
          {
            op: "replace",
            value: name,
            path: "/name",
          },
          {
            op: "replace",
            value: description,
            path: "/description",
          },
          {
            op: "replace",
            value: adminSwitch2,
            path: "/type",
          },
          {
            op: "replace",
            value: active,
            path: "/active",
          },
          {
            op: "replace",
            value: confidentialityLevel,
            path: "/confidentialityLevel",
          },
          {
            op: "2",
            value: LogOperation.getDevice(),
            path: "device",
          },
          {
            op: "2",
            value: LogOperation.getMachineIP(),
            path: "ip",
          },
          {
            op: "2",
            value: LogOperation.getPoliceUnitId(),
            path: "logPoliceUnitId",
          },
          {
            op: "2",
            value: LogOperation.getUserId(),
            path: "logUserId",
          },
        ],
        {
          params: {
            id: profileId,
          },
        },
      );

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  deleteAction: async (
    authToken: string,
    systemObjectUserCustomerProfileActionId: any,
  ) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    if (!systemObjectUserCustomerProfileActionId) {
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };
    }

    try {
      const { data } = await api.delete<any>(
        `v1/SystemObjectUserCustomerProfileAction/Delete`,
        {
          params: {
            id: systemObjectUserCustomerProfileActionId,
          },
        },
      );

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  deleteCustom: async (
    authToken: string,
    systemObjectUserCustomerProfileActionId: any,
  ) => {
    if (!authToken) {
      return {
        status: false,
        message: "Não é possível inativar o registro sem o token correto.",
      };
    }

    if (!systemObjectUserCustomerProfileActionId) {
      return {
        status: false,
        message: "Não é possível criar ações sem os dados corretos.",
      };
    }

    try {
      const { data } = await api.delete<any>(
        `v1/SystemObjectUserCustomerProfileAction/DeleteCustom`,
        {
          params: {
            id: systemObjectUserCustomerProfileActionId,
          },
        },
      );

      return {
        status: true,
        data: data.data,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
  GetAllMenuObjectsByUserCustomerProfile: async (
    custumerId: string,
    UserCustomerProfileId: string,
  ) => {
    try {
      const { data } = await api.get(
        `/v1/SystemObjectUserCustomerProfile/GetAllMenuObjectsByUserCustomerProfile?customerId=${custumerId}&UserCustomerProfileId=${UserCustomerProfileId}`,
      );
      return data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        const messages = error?.response?.data?.errors;
        const { severity, status, message, data } = returnResponse({
          statusCode: error.response?.status,
          data: error?.response?.data,
          message: Array.isArray(messages)
            ? messages.at(0)?.message
            : "An unexpected error occurred",
        });

        return {
          severity,
          status,
          message: message ? message : "Error",
          data,
        };
      }

      return {
        status: false,
        message: "Error",
        data: null,
        severity: "error",
      };
    }
  },
};
